import { CheckCircle2, XCircle, Flag, Send, ArrowLeft, DoorOpen } from "lucide-react";

export const ReviewScreen = ({ es, onNavigate, onStartWalk, onOpenSubmit, onOpenEndSection, onBack }) => {
  const testing = es.mode === "testing";
  const lo = testing ? es.section_start || 1 : 1;
  const hi = testing ? es.section_end || es.total_questions : es.total_questions;
  const nums = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  const answered = new Set(Object.keys(es.answers).map(Number));
  const marked = new Set(es.marked);
  const answeredHere = nums.filter((n) => answered.has(n)).length;
  const markedHere = nums.filter((n) => marked.has(n)).length;
  const incompleteCount = nums.length - answeredHere;
  const finalSection = !testing || (es.section || 1) >= (es.sections_total || 3);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-32">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Review Screen</h2>
          <p className="text-sm text-slate-500 mt-1">
            {testing && <span className="font-medium text-slate-700">Section {es.section} of {es.sections_total} (Questions {lo}–{hi}) · </span>}
            {answeredHere} answered · {incompleteCount} incomplete · {markedHere} marked for review
          </p>
        </div>
        <button
          data-testid="back-to-question-btn"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-md hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to question
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button data-testid="review-all-btn" onClick={() => onStartWalk("all")}
          className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors">
          Review All
        </button>
        <button data-testid="review-marked-btn" onClick={() => onStartWalk("marked")}
          className="text-sm font-medium px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 transition-colors">
          Review Marked ({markedHere})
        </button>
        <button data-testid="review-incomplete-btn" onClick={() => onStartWalk("incomplete")}
          className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors">
          Review Incomplete ({incompleteCount})
        </button>
        <div className="flex-1" />
        {finalSection ? (
          <button data-testid="open-submit-modal-btn" onClick={onOpenSubmit}
            className="flex items-center gap-1.5 text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors">
            <Send className="w-4 h-4" /> {es.mode === "study" ? "Finish and Score" : "Submit Exam"}
          </button>
        ) : (
          <button data-testid="end-section-btn" onClick={onOpenEndSection}
            title="Submit this section and take an optional 10-minute break — you can't return to these questions"
            className="flex items-center gap-1.5 text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors">
            <DoorOpen className="w-4 h-4" /> End Section {es.section}
          </button>
        )}
      </div>

      {testing && !finalSection && (
        <p data-testid="end-section-note" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Ending this section is final — you won't be able to return to questions {lo}–{hi}. An optional
          10-minute break follows (exam clock paused; overage is deducted).
        </p>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-500 mb-4">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-800 inline-block" /> Answered</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border border-slate-300 inline-block" /> Unanswered</span>
        <span className="flex items-center gap-1.5"><Flag className="w-3 h-3 text-amber-500 fill-amber-400" /> Marked</span>
      </div>

      <div data-testid="review-grid" className="grid grid-cols-6 sm:grid-cols-10 lg:grid-cols-[repeat(15,minmax(0,1fr))] gap-2">
        {nums.map((n) => {
          const isAns = answered.has(n);
          const isMk = marked.has(n);
          return (
            <button
              key={n}
              data-testid={`review-tile-${n}`}
              onClick={() => onNavigate(n)}
              className={`relative h-10 rounded-md border text-xs font-mono font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                isAns
                  ? "bg-slate-800 text-white border-slate-800 hover:bg-slate-700"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              } ${isMk ? "ring-2 ring-amber-400 ring-offset-1" : ""}`}
            >
              {n}
              {isMk && <Flag className="w-2.5 h-2.5 text-amber-500 fill-amber-400 absolute top-0.5 right-0.5" />}
            </button>
          );
        })}
      </div>

      {es.mode === "study" && (
        <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            {Object.values(es.checked || {}).filter((c) => c.is_correct).length} correct
          </span>
          <span className="flex items-center gap-1.5">
            <XCircle className="w-4 h-4 text-rose-500" />
            {Object.values(es.checked || {}).filter((c) => !c.is_correct).length} incorrect
          </span>
          <span>{Object.keys(es.checked || {}).length} checked</span>
        </div>
      )}
    </div>
  );
};
